import { CommonModule, DecimalPipe } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewEncapsulation } from '@angular/core';
import { RouterLink } from '@angular/router';
import { AnimationItem } from 'lottie-web';
import { LottieComponent } from 'ngx-lottie';
import { firstValueFrom } from 'rxjs';
import { LocalMortgageSimulation } from '../../models';
import { LocalDataKey, LocalStorageService } from '../../services/local-storage/local-storage.service';
import { PlatformService } from '../../services/platform/platform.service';
import { LocalUser } from '../../services/users/users.service';
import { ButtonComponent } from '../button/button.component';

export type FloatingInterceptorType =
  | 'ecommerce-fathers-day-promotion'
  | 'ecommerce-hot-sale-promotion'
  | 'ecommerce-mothers-day-promotion'
  | 'ecommerce-promotion'
  | 'enrollment-new-account-promotion'
  | 'enrollment-promotion'
  | 'home-assistance-promotion'
  | 'mortgage-simulator-promotion';

@Component({
  selector: 'app-floating-interceptor',
  templateUrl: './floating-interceptor.component.html',
  styleUrls: ['./floating-interceptor.component.scss'],
  standalone: true,
  imports: [
    ButtonComponent,
    CommonModule,
    DecimalPipe,
    LottieComponent,
    RouterLink,
  ],
  encapsulation: ViewEncapsulation.None,
})
export class FloatingInterceptorComponent implements OnInit, OnChanges {
  @Input() location: string;
  protected days = 0;
  protected finished = false;
  protected hours = 0;
  protected isClosing = false;
  protected isOpen = false;
  protected isToggled = false;
  protected minutes = 0;
  protected offerType: FloatingInterceptorType = 'mortgage-simulator-promotion';
  // TODO: refactor so each offer has its own query params
  protected queryParamsEcommercePromotion: Record<string, string> = {
    filters: JSON.stringify([
      { field: 'tags', operator: 'in', value: 'campaigns' },
    ]),
  };
  protected seconds = 0;
  protected showOffer = false;
  private animation: AnimationItem;
  private finishAt: Date | null = null; // should be null when the offer haven't finish date
  private hideBirdOffer: boolean;
  private timerInterval: NodeJS.Timeout;

  constructor(
    private localStorageService: LocalStorageService,
    private platformService: PlatformService,
  ) {}

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (!changes['location'] || !changes['location'].currentValue) {
      return;
    }
    if (this.offerType === 'home-assistance-promotion') {
      this.showOffer = changes['location'].currentValue !== '/survey';
    } else if (this.offerType.startsWith('enrollment')) {
      const isEnrollmentPage = changes['location'].currentValue.startsWith('/enrollment');
      const localUser = await firstValueFrom(this.localStorageService.get<LocalUser>(LocalDataKey.LocalUser));
      if (this.offerType === 'enrollment-promotion') {
        this.showOffer = !isEnrollmentPage && !localUser?.bankValidated;
      } else if (this.offerType === 'enrollment-new-account-promotion') {
        this.showOffer = !isEnrollmentPage && !localUser;
      }
    } else if (this.offerType.startsWith('ecommerce')) {
      this.showOffer = changes['location'].currentValue !== '/benefits';
    } else if (this.offerType === 'mortgage-simulator-promotion') {
      const currentMortgageSimulation = await firstValueFrom(this.localStorageService.get<LocalMortgageSimulation>(LocalDataKey.MortgageSimulation));
      this.showOffer = !currentMortgageSimulation && !(
        changes['location'].currentValue.startsWith('/desarrolladores') ||
        changes['location'].currentValue.startsWith('/ecommerce') ||
        changes['location'].currentValue.startsWith('/home-assistance') ||
        changes['location'].currentValue.startsWith('/mortgage-simulator') ||
        changes['location'].currentValue.startsWith('/moving') ||
        changes['location'].currentValue.startsWith('/remodeling') ||
        changes['location'].currentValue.startsWith('/warehouse')
      );
    }
  }

  async ngOnInit(): Promise<void> {
    if (this.platformService.isServer()) {
      return;
    }
    this.startTimer();
    // TODO: refactor so each offer has its own hide bird offer key
    // also going back and forth between offers should reset the hide bird offer key
    this.hideBirdOffer = !!(await firstValueFrom(this.localStorageService.get<boolean>(LocalDataKey.HideBirdOffer)));
  }

  protected close(): void {
    if (this.animation) {
      this.animation.setDirection(-1);
      this.animation.play();
    }
    this.isClosing = true;
    this.localStorageService.set(LocalDataKey.HideBirdOffer, true);
    this.hideBirdOffer = true;
    setTimeout(() => {
      this.isOpen = false;
      this.isClosing = false;
    }, 300);
  }

  protected onAnimationCreated(animation: AnimationItem): void {
    this.animation = animation;
    if (this.hideBirdOffer) {
      this.isOpen = false;
      return;
    }
    this.toggleExpand();
  }

  protected onMouseEnter(): void {
    if (!this.animation || this.isOpen) {
      return;
    }
    this.animation.setDirection(1);
    this.animation.play();
  }

  protected onMouseLeave(): void {
    if (!this.animation || this.isOpen) {
      return;
    }
    this.animation.setDirection(-1);
    this.animation.play();
  }

  protected toggleExpand(): void {
    if (!this.animation) {
      return;
    }
    this.isOpen ? this.close() : this.open();
    this.isToggled = !this.isToggled;
  }

  private calculateTimeRemaining(): void {
    if (!this.finishAt) {
      return;
    }
    const now = new Date().getTime();
    const timeLeft = this.finishAt.getTime() - now;
    if (timeLeft < 0) {
      clearInterval(this.timerInterval);
      this.days = 0;
      this.hours = 0;
      this.minutes = 0;
      this.seconds = 0;
      this.finished = true;
      return;
    }
    this.days = Math.floor(timeLeft / (1000 * 60 * 60 * 24));
    this.hours = Math.floor((timeLeft % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    this.minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
    this.seconds = Math.floor((timeLeft % (1000 * 60)) / 1000);
  }

  private open(): void {
    this.animation.setDirection(1);
    this.animation.play();
    this.isOpen = true;
    this.localStorageService.set(LocalDataKey.HideBirdOffer, false);
    this.hideBirdOffer = false;
  }

  private startTimer(): void {
    this.calculateTimeRemaining();
    this.timerInterval = setInterval(() => this.calculateTimeRemaining(), 1000);
  }
}